/**
 * lib/missingContactsPdf.ts
 * ------------------------------------------------------------------
 * ONE SHEET PER CLASS TEACHER: the families in their class whose email
 * address the school does not have.
 *
 * In the finance house style (jsPDF, navy letterhead, SCHOOL config) like
 * invoicePdf and statementPdf, but this one is not a finance document and
 * deliberately does not read like one. It carries NO money. A class teacher
 * is being asked to collect an address, not told which of their pupils'
 * families are behind on fees — see lib/missingContacts for that decision.
 *
 * It is designed to be printed and written on: a blank ruled line after each
 * pupil, because the answer will come back on paper or in a corridor long
 * before anyone types it into the portal.
 * ------------------------------------------------------------------ */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import type { MissingContactPack } from '@/lib/missingContacts';

export interface MissingContactsPdfOptions {
  logoDataUrl?: string | null;
  /** Printed in the footer so a teacher knows how current the sheet is. */
  checkedAt?: string;
}

const NAVY: [number, number, number] = [27, 58, 107];
const INK: [number, number, number] = [31, 41, 55];
const MUTED: [number, number, number] = [107, 114, 128];
const RULE: [number, number, number] = [209, 213, 219];
const AMBER: [number, number, number] = [180, 83, 9];

export function buildMissingContactsPdf(
  pack: MissingContactPack,
  opt: MissingContactsPdfOptions = {},
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 16; const right = 210 - MX;
  const BOTTOM = 297 - 18;
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  // ---- letterhead ----
  if (opt.logoDataUrl) {
    try { pdf.addImage(opt.logoDataUrl, 'PNG', MX, y - 2, 18, 18); } catch { /* ignore */ }
  }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text(SCHOOL.name, MX + 22, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(
    `${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}`,
    MX + 22, y + 9,
  );
  y += 22;
  setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 8;

  // ---- title ----
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text('PARENT EMAIL ADDRESSES NEEDED', MX, y);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); setT(INK);
  pdf.text(pack.className, MX, y + 6.5);
  if (pack.teacherName) {
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); setT(MUTED);
    pdf.text(`Class teacher: ${pack.teacherName}`, MX, y + 11.5);
    y += 16;
  } else {
    y += 10;
  }

  // ---- what this is, and what to do with it ----
  const intro = pack.note
    ? `${pack.note} The school has no working email address for the ${pack.pupils.length} `
      + `${pack.pupils.length === 1 ? 'pupil' : 'pupils'} below.`
    : `The school has no working email address for the ${pack.pupils.length} `
      + `${pack.pupils.length === 1 ? 'pupil' : 'pupils'} below, so none of them `
      + 'receive reports, invoices or school notices by email. Please ask the parent '
      + 'for a current address and write it on the line provided, then return this '
      + 'sheet to the office.';
  setF([248, 250, 252]); setD(RULE); pdf.setLineWidth(0.3);
  const introLines = pdf.splitTextToSize(intro, right - MX - 6) as string[];
  const introH = 6 + introLines.length * 4;
  pdf.rect(MX, y, right - MX, introH, 'FD');
  setT(INK); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  introLines.forEach((line, i) => pdf.text(line, MX + 3, y + 5.5 + i * 4));
  y += introH + 6;

  if (pack.withNothing > 0) {
    /* The ones with no telephone either cannot be reached by ANY means, and
       they are listed first for that reason. Saying so stops a teacher
       reading the order as alphabetical and missing the point. */
    setT(AMBER); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5);
    pdf.text(
      `${pack.withNothing} of these ${pack.withNothing === 1 ? 'has' : 'have'} no telephone number either — `
      + 'listed first, marked ▲.',
      MX, y,
    );
    y += 6;
  }

  // ---- the pupils ----
  const rowFor = (n: number) => 15 + n * 4;

  pack.pupils.forEach((p, i) => {
    const guardianLines = p.guardianNames.length
      ? (pdf.splitTextToSize(`Guardian: ${p.guardianNames.join(', ')}`, right - MX - 8) as string[])
      : [];
    const needed = rowFor(guardianLines.length);
    if (y + needed > BOTTOM) { pdf.addPage(); y = 18; }

    // Name line, with the marker for anyone unreachable by any means.
    setT(INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
    const mark = p.hasPhone ? '' : '▲ ';
    pdf.text(`${mark}${i + 1}.  ${p.name}`, MX, y);
    if (p.admissionNumber) {
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
      pdf.text(`Adm ${p.admissionNumber}`, right, y, { align: 'right' });
    }
    y += 4.5;

    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
    guardianLines.forEach((line) => { pdf.text(line, MX + 5, y); y += 4; });

    if (p.phones.length) {
      setT(INK); pdf.text(`Phone: ${p.phones.join('   /   ')}`, MX + 5, y);
    } else {
      setT(AMBER); pdf.text('No telephone number on file either.', MX + 5, y);
    }
    y += 4.5;

    /* The address we hold, so a teacher can see it is not a typo to correct
       but a fiction to replace — every one of these was invented by the
       import from the pupil's own surname. */
    if (p.heldEmail) {
      setT(MUTED); pdf.setFontSize(7.5);
      pdf.text(`On file (does not work): ${p.heldEmail}`, MX + 5, y);
      y += 4;
    }

    // The line the answer gets written on.
    setT(MUTED); pdf.setFontSize(8);
    pdf.text('Email:', MX + 5, y + 3.5);
    setD(RULE); pdf.setLineWidth(0.25);
    pdf.line(MX + 18, y + 4, right, y + 4);
    y += 10;
  });

  // ---- footer on every page ----
  const pages = pdf.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5);
    const stamp = opt.checkedAt ? `Checked ${opt.checkedAt.slice(0, 10)}` : '';
    pdf.text(
      [pack.className, stamp].filter(Boolean).join('  ·  '),
      MX, 297 - 10,
    );
    pdf.text(`Page ${i} of ${pages}`, right, 297 - 10, { align: 'right' });
  }

  return pdf;
}
